import { ErrorMessage, Field, Form, Formik } from "formik";
import moment from "moment";
import "moment-timezone";
import React from "react";
import { Button, ControlLabel } from "react-bootstrap";
import shortid from "shortid";
import * as Yup from "yup";
import FormikDatetime from "../../../Shared/FormikDateTime";
import FormikInputControl from "../../../Shared/Forms/FormikInputControl";
import { formats } from '../../../variables/variables';

const AddAvailabilityException = ({ add }) => {
  const StartEnd = ({ index, errors, touched }) => {
    const fromError = errors.hours && errors.hours[index] && errors.hours[index].from;
    const toError = errors.hours && errors.hours[index] && errors.hours[index].to;

    return (
      <div style={{ display: "flex", marginRight: 30 }}>
        <FormikInputControl
          fieldLabel="Start"
          fieldName={`hours.${index}.from`}
          errors={errors}
          touched={touched}
          style={{ width: 60 }}
          placeholder="00:00"
          className={`form-group ${fromError && "has-error"}`}
        />
        <span style={{ margin: "30px 5px 0 5px" }}>to</span>
        <FormikInputControl
          fieldLabel="End"
          fieldName={`hours.${index}.to`}
          errors={errors}
          touched={touched}
          style={{ width: 60 }}
          placeholder="00:00"
          className={`form-group ${toError && "has-error"}`}
        />
      </div>
    );
  }

  const minDate = moment().startOf("day");
  const validationSchema = Yup.object().shape({
    description: Yup.string().max(50, 'max 50 chars'),
    from_date: Yup.date()
      .required("is required")
      .min(minDate, `must be on or after today`),
    to_date: Yup.date()
      .min(Yup.ref("from_date"), "must be on or after from date")
      .required("is required"),
    hours: Yup.array().of(
      Yup.object().shape({
        from: Yup.string().matches(
          /^$|^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
          "HH:MM"
        ),
        to: Yup.string().matches(
          /^$|^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
          "HH:MM"
        ),
      })
    ),
  });

  const submitForm = (values, { setSubmitting, setFieldError, validateForm, ...props }) => {
    const hoursBoth = (idx) => {
      if (values.hours[idx]) {
        const from = values.hours[idx].from;
        const to = values.hours[idx].to;
        if (from && from.length > 0 && (!to || to.length === 0)) {
          return false;
        }
        if (to && to.length > 0 && (!from || from.length === 0)) {
          return false;
        }
        if (from === to && from && to && from.length > 0 && to.length > 0) return false;
      }
      return true;
    };

    const hoursBothValid =
      hoursBoth(0) && hoursBoth(1) && hoursBoth(2) && hoursBoth(3);
    if (!hoursBothValid)
      setFieldError(
        "form",
        "Start and End hours must both be provided, and not be the same"
      );

    if (hoursBothValid) validateForm().then(() => {
        const result = {
          id: shortid.generate(),
          date: values.from_date.format(formats.consistentDateFormat),
          date_to: values.to_date.format(formats.consistentDateFormat),
          description: values.description,
          available: values.hours
            .map((d) => { return d.from && d.to && d.from.length > 0 && d.to.length > 0 ? { from: d.from, to: d.to } : null })
            .filter((d) => d !== null),
        };

        add(result);
      })

    setSubmitting(false);
  };

  return (
    <div style={{minWidth:250}}>
      <h4>Add Availability Override</h4>

      <Formik
        initialValues={{
          to_date: moment(),
          from_date: moment(),
          hours: [],
          description: "",
        }}
        validationSchema={validationSchema}
        onSubmit={submitForm}
        render={({
          errors,
          touched,
          isValid,
          isSubmitting,
          values,
          setFieldValue,
          setFieldTouched,
          validateForm,
        }) => {
          const hasError = (key) => {
            return errors[key] && touched[key] && "has-error";
          };

          return (
            <Form style={{marginBottom:20}}>
              <div style={{ display: "flex", marginRight: 30 }}>
                <div className={`form-group ${hasError("from_date")}`}>
                  <ControlLabel>From Date</ControlLabel>
                  <FormikDatetime
                    className="datetime-input-small"
                    field="from_date"
                    input={{
                      value: moment(values.from_date).format("DD MMM YYYY"),
                      onChange: setFieldValue,
                      onBlur: setFieldTouched,
                    }}
                    closeOnSelect={true}
                    dateFormat="DD MMM YYYY"
                    timeFormat={false}
                  />
                  <ErrorMessage
                    name="from_date"
                    component="span"
                    className="help-block"
                  />
                </div>
              </div>
              <div style={{ display: "flex" }}>
                <div className={`form-group ${hasError("to_date")}`}>
                  <ControlLabel>To Date</ControlLabel>
                  <FormikDatetime
                    className="datetime-input-small"
                    field="to_date"
                    input={{
                      value: moment(values.to_date).format("DD MMM YYYY"),
                      onChange: setFieldValue,
                      onBlur: setFieldTouched,
                    }}
                    closeOnSelect={true}
                    dateFormat="DD MMM YYYY"
                    timeFormat={false}
                  />
                  <ErrorMessage
                    name="to_date"
                    component="span"
                    className="help-block"
                  />
                </div>
              </div>
              <div style={{ display: "flex" }}>
                <StartEnd index={0} errors={errors} touched={touched} />
                <StartEnd index={1} errors={errors} touched={touched} />
              </div>
              <div style={{ display: "flex" }}>
                <StartEnd index={2} errors={errors} touched={touched} />
                <StartEnd index={3} errors={errors} touched={touched} />
              </div>
              <div className={`form-group ${hasError("description")}`}>
                <ControlLabel>Description</ControlLabel>
                <Field
                  type="text"
                  name="description"
                  className="form-control form-control-sm"
                />
                <ErrorMessage
                  name="description"
                  component="span"
                  className="help-block"
                />
              </div>

              <div className={`form-group ${errors["form"] && "has-error"}`}>
                <span className="help-block">{errors && errors["form"]}</span>
              </div>

              <Button
                disabled={isSubmitting}
                type="submit"
                bsStyle="info"
                bsSize="small"
              >
                Add Override
              </Button>
            </Form>
          );
        }}
      />
    </div>
  );
};

export default AddAvailabilityException;
